/**
 * AAR replay overlay (#180) — the scrub chrome for the incident time replay.
 *
 * PART IX posture: contextual-only — exists ONLY while the operator has
 * explicitly entered replay from an incident panel; nothing persistent joins
 * the COP. Honest chrome: the surface is loudly labelled NOT LIVE (amber
 * banner) and states that the live urgent-alert layers are hidden while it is
 * open (App suppresses them; every other live layer keeps flowing).
 *
 * Law 6: scrub-only. The slider and the step-to-event buttons are discrete
 * operator-triggered moves — there is deliberately NO autoplay loop, which
 * would be ongoing ambient animation outside the motion budget.
 */
import { useMemo, useState, type JSX } from 'react';
import type { AarReplayResponse } from '@tsr/shared-types';
import {
  buildReplayTimeline,
  eventCountAt,
  clampReplayTime,
  trackCoverageNote,
  type AarTimelineEvent,
} from './aarReplayTimeline';

function clock(tMs: number): string {
  return new Date(tMs).toISOString().slice(0, 19).replace('T', ' ') + 'Z';
}

function shortClock(tMs: number): string {
  return new Date(tMs).toISOString().slice(11, 19) + 'Z';
}

export function AarReplayOverlay({
  data,
  onSeek,
  onExit,
}: {
  data: AarReplayResponse;
  /** Scrub position changed (epoch ms, already clamped). */
  onSeek: (tMs: number) => void;
  onExit: () => void;
}): JSX.Element {
  const timeline = useMemo(() => buildReplayTimeline(data), [data]);
  const [tMs, setTMs] = useState(timeline.startMs);

  const seek = (raw: number): void => {
    const t = clampReplayTime(timeline, raw);
    setTMs(t);
    onSeek(t);
  };

  const occurred = eventCountAt(timeline.events, tMs);
  const span = timeline.endMs - timeline.startMs;
  const trackNote = trackCoverageNote(data);

  const stepEvent = (dir: -1 | 1): void => {
    // ⏮ jumps to the previous event's moment; ⏭ to the next one's.
    const target: AarTimelineEvent | undefined =
      dir === 1 ? timeline.events[occurred] : timeline.events[occurred - 2];
    if (target) seek(target.tMs);
    else if (dir === -1) seek(timeline.startMs);
  };

  return (
    <div
      data-testid="aar-replay-overlay"
      className="absolute bottom-3 left-1/2 z-[75] w-[min(680px,calc(100%-24px))] -translate-x-1/2 rounded-lg border border-amber-500/70 bg-slate-900/95 shadow-2xl backdrop-blur"
    >
      {/* ── NOT-LIVE banner ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 rounded-t-lg bg-amber-500/15 px-3 py-1.5">
        <span className="text-[11px] font-bold tracking-widest text-amber-400">
          ⏪ AAR REPLAY — NOT LIVE
        </span>
        <span className="font-mono text-[11px] text-slate-200">
          {data.incident.incident_number}
        </span>
        <span className="truncate text-[11px] text-slate-400">{data.incident.name}</span>
        <button
          type="button"
          data-testid="aar-replay-exit"
          onClick={onExit}
          className="ml-auto rounded border border-amber-500/60 px-2 py-0.5 text-[11px] font-medium text-amber-300 hover:bg-amber-500/20"
        >
          Exit replay
        </button>
      </div>
      <p className="px-3 pt-1 text-[10px] text-slate-500">
        Live urgent-alert layers are hidden while replaying — every other live layer keeps
        flowing. {!timeline.closed && 'Incident still open: replaying up to now. '}
        {timeline.gaps.length > 0 &&
          `Shaded span${timeline.gaps.length === 1 ? '' : 's'}: the incident was deactivated, then reactivated. `}
        {data.events_truncated && `Event list capped at ${data.events.length} (oldest kept). `}
      </p>

      {/* ── Scrubber ────────────────────────────────────────────────── */}
      <div className="px-3 pb-1 pt-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="aar-replay-prev-event"
            onClick={() => stepEvent(-1)}
            disabled={occurred === 0}
            title="Previous event"
            className="rounded border border-slate-600 px-1.5 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-40"
          >
            ⏮
          </button>
          <input
            type="range"
            data-testid="aar-replay-slider"
            min={timeline.startMs}
            max={timeline.endMs}
            step={1000}
            value={tMs}
            onChange={(e) => seek(Number(e.target.value))}
            aria-label="Replay time"
            className="h-1.5 flex-1 cursor-pointer accent-amber-500"
          />
          <button
            type="button"
            data-testid="aar-replay-next-event"
            onClick={() => stepEvent(1)}
            disabled={occurred >= timeline.events.length}
            title="Next event"
            className="rounded border border-slate-600 px-1.5 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-40"
          >
            ⏭
          </button>
          <span data-testid="aar-replay-time" className="w-40 text-right font-mono text-[11px] text-amber-300">
            {clock(tMs)}
          </span>
        </div>
        {/* #485 — deactivation gap band(s): the incident was deactivated then reactivated. Shaded
            so a reopened incident's clock is not read as continuously active (a HARD ❌). */}
        {timeline.gaps.length > 0 && span > 0 && (
          <div className="relative mt-0.5 h-1.5" data-testid="aar-replay-gap-band">
            {timeline.gaps.map((g) => {
              const leftPct = ((g.startMs - timeline.startMs) / span) * 100;
              const widthPct = ((g.endMs - g.startMs) / span) * 100;
              if (!Number.isFinite(leftPct) || !Number.isFinite(widthPct) || widthPct <= 0)
                return null;
              return (
                <div
                  key={`${g.startMs}-${g.endMs}`}
                  data-testid="aar-replay-gap"
                  title={`Deactivated ${clock(g.startMs)} → reactivated ${clock(g.endMs)}`}
                  className="absolute top-0 h-1.5 rounded-sm bg-slate-500/60 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(148,163,184,0.5)_3px,rgba(148,163,184,0.5)_6px)]"
                  style={{
                    left: `${Math.max(0, Math.min(100, leftPct))}%`,
                    width: `${Math.max(0.5, Math.min(100, widthPct))}%`,
                  }}
                />
              );
            })}
          </div>
        )}
        {/* Operational-period ticks under the track. */}
        {timeline.periods.length > 0 && span > 0 && (
          <div className="relative mt-0.5 h-3.5">
            {timeline.periods.map((p) => {
              const leftPct = ((Math.max(p.startMs, timeline.startMs) - timeline.startMs) / span) * 100;
              if (!Number.isFinite(leftPct) || leftPct < 0 || leftPct > 100) return null;
              return (
                <button
                  type="button"
                  key={p.id}
                  data-testid={`aar-replay-period-tick-${p.period_number}`}
                  onClick={() => seek(p.startMs)}
                  title={`Period ${p.period_number} · ${p.start_at} → ${p.end_at}`}
                  className="absolute -translate-x-1/2 text-[9px] text-slate-400 hover:text-amber-300"
                  style={{ left: `${leftPct}%` }}
                >
                  ▲P{p.period_number}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Synchronized event list ─────────────────────────────────── */}
      <div className="max-h-36 overflow-y-auto border-t border-slate-700/70 px-1 py-1">
        {timeline.events.length === 0 ? (
          <p className="px-2 py-1 text-[11px] text-slate-500" data-testid="aar-replay-no-events">
            No audit actions recorded inside this incident&apos;s window.
          </p>
        ) : (
          timeline.events.map((e, i) => {
            const isPast = i < occurred;
            const isCurrent = i === occurred - 1;
            return (
              <button
                type="button"
                key={e.event.id}
                data-testid={`aar-replay-event-row-${i}`}
                onClick={() => seek(e.tMs)}
                className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-slate-800/60 ${
                  isCurrent ? 'border-l-2 border-amber-400 bg-slate-800/50' : ''
                } ${isPast ? '' : 'opacity-40'}`}
              >
                <span className="font-mono text-[10px] text-slate-400">{shortClock(e.tMs)}</span>
                <span className="text-[11px] text-slate-200">{e.event.verb_label}</span>
                {e.event.target && (
                  <span className="truncate text-[10px] text-slate-500">{e.event.target}</span>
                )}
                <span className="ml-auto shrink-0 text-[9px] text-slate-600">
                  {e.event.operator_id}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* ── Honest-data footnote ────────────────────────────────────── */}
      <p
        data-testid="aar-replay-track-note"
        className="rounded-b-lg border-t border-slate-800 px-3 py-1 text-[9px] leading-snug text-slate-600"
      >
        Replays recorded audit actions, anomaly lifecycle timestamps and stored aircraft/vessel
        tracks. {trackNote} Expired hotspot rows may be absent; a hotspot&apos;s
        &ldquo;expired&rdquo; end is its projected TTL, not an observed clear.
      </p>
    </div>
  );
}
